import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { Ban, HelpCircle, KeyRound, Pencil, PlayCircle, Trash2, UserPlus } from 'lucide-react'
import './AdminSettings.scss'
import Sidebar from '../../components/Sidebar/Sidebar'
import { drumtaleAdminSessionAtom, drumtaleAdminTokenAtom } from '../../AtomStore'
import { openPageSpecification } from '../shared/pageSpecification'
import { adminSettingsDataModelGuide, adminSettingsImplementationGuide, adminSettingsSpecification } from './adminSettingsSpecification'
import { menuLabel, resolveAllowedMenus, roleLabel, ROLE_OPTIONS } from './adminSettingsConstants'
import RoleMenuFields from './RoleMenuFields'
import MainURL from '../../MainURL'

const TOAST_DURATION_MS = 2400

const AUDIT_ACTION_LABELS: Record<string, string> = {
  create: '부계정 등록',
  role_update: '권한 수정',
  suspend: '사용중지',
  resume: '사용중지 해제',
  delete: '계정 삭제',
  super_admin_grant: '최고관리자 지정',
  super_admin_revoke: '최고관리자 해제',
  signup_approve: '가입 승인',
  signup_reject: '가입 거절',
  password_change: '비밀번호 변경',
}

interface PendingAdmin {
  id: number
  email: string
  name: string
  signup_ip: string | null
}

interface AdminAccount {
  id: number
  email: string
  name: string
  is_active: number
  is_super_admin: number
  is_suspended: number
  role: string
  allowed_menus: string | null
  signup_ip: string | null
  last_login_at: string | null
  created_at: string | null
}

interface AuditLogEntry {
  created_at: string
  actor_label: string
  target_label: string
  action: string
  detail: string | null
  result: 'success' | 'failure'
}

interface CurrentAdmin {
  email: string
  name: string
}

function adminStatusLabel(admin: AdminAccount): '승인 대기' | '사용중지' | '사용중' {
  if (!admin.is_active) {
    return '승인 대기'
  }
  return admin.is_suspended ? '사용중지' : '사용중'
}

function adminStatusBadgeClass(admin: AdminAccount): string {
  if (admin.is_active && !admin.is_suspended) {
    return 'active'
  }
  return admin.is_suspended ? 'suspended' : ''
}

function AdminSettings() {
  const navigate = useNavigate()
  const isAdminSessionActive = useAtomValue(drumtaleAdminSessionAtom)
  const adminToken = useAtomValue(drumtaleAdminTokenAtom)

  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  const [currentAdmin, setCurrentAdmin] = useState<CurrentAdmin | null>(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null)

  const [pendingAdmins, setPendingAdmins] = useState<PendingAdmin[]>([])
  const [isLoadingPendingAdmins, setIsLoadingPendingAdmins] = useState(false)
  const [processingAdminId, setProcessingAdminId] = useState<number | null>(null)

  const [allAdmins, setAllAdmins] = useState<AdminAccount[]>([])
  const [isLoadingAllAdmins, setIsLoadingAllAdmins] = useState(false)
  const [processingSuperAdminId, setProcessingSuperAdminId] = useState<number | null>(null)
  const [processingLifecycleId, setProcessingLifecycleId] = useState<number | null>(null)

  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([])
  const [isLoadingAuditLog, setIsLoadingAuditLog] = useState(false)

  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [isPasswordFormOpen, setIsPasswordFormOpen] = useState(false)
  const [currentPasswordInput, setCurrentPasswordInput] = useState('')
  const [newPasswordInput, setNewPasswordInput] = useState('')
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('')
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false)

  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false)
  const [createEmail, setCreateEmail] = useState('')
  const [createName, setCreateName] = useState('')
  const [createPassword, setCreatePassword] = useState('')
  const [createRole, setCreateRole] = useState('all')
  const [createAllowedMenus, setCreateAllowedMenus] = useState<string[]>([])
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false)

  const [editingAdminId, setEditingAdminId] = useState<number | null>(null)
  const [editRole, setEditRole] = useState('all')
  const [editAllowedMenus, setEditAllowedMenus] = useState<string[]>([])
  const [isSubmittingRoleEdit, setIsSubmittingRoleEdit] = useState(false)

  useEffect(() => {
    if (!isAdminSessionActive) {
      navigate('/')
    }
  }, [isAdminSessionActive, navigate])

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${adminToken}` }), [adminToken])
  const jsonAuthHeaders = useMemo(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` }),
    [adminToken],
  )

  const loadCurrentAdmin = useCallback(async () => {
    if (!adminToken) {
      return
    }
    try {
      const response = await fetch(`${MainURL}/admin/me`, { headers: authHeaders })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '관리자 정보를 불러오지 못했습니다.')
        return
      }
      setCurrentAdmin({ email: data.email, name: data.name })
      setIsSuperAdmin(!!data.is_super_admin)
    } catch (error) {
      showToast('관리자 정보를 불러오지 못했습니다.')
    }
  }, [adminToken, authHeaders])

  const loadPendingAdmins = useCallback(async () => {
    if (!adminToken) {
      return
    }
    setIsLoadingPendingAdmins(true)
    try {
      const response = await fetch(`${MainURL}/admin/pending-admins`, { headers: authHeaders })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '가입 승인 대기 목록을 불러오지 못했습니다.')
        return
      }
      setPendingAdmins(data?.pendingAdmins ?? [])
    } catch (error) {
      showToast('가입 승인 대기 목록을 불러오지 못했습니다.')
    } finally {
      setIsLoadingPendingAdmins(false)
    }
  }, [adminToken, authHeaders])

  const loadAllAdmins = useCallback(async () => {
    if (!adminToken) {
      return
    }
    setIsLoadingAllAdmins(true)
    try {
      const response = await fetch(`${MainURL}/admin/admins`, { headers: authHeaders })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '관리자 목록을 불러오지 못했습니다.')
        return
      }
      setAllAdmins(data?.admins ?? [])
    } catch (error) {
      showToast('관리자 목록을 불러오지 못했습니다.')
    } finally {
      setIsLoadingAllAdmins(false)
    }
  }, [adminToken, authHeaders])

  const loadAuditLog = useCallback(async () => {
    if (!adminToken) {
      return
    }
    setIsLoadingAuditLog(true)
    try {
      const response = await fetch(`${MainURL}/admin/audit-log`, { headers: authHeaders })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '변경 이력을 불러오지 못했습니다.')
        return
      }
      setAuditLog(data?.auditLog ?? [])
    } catch (error) {
      showToast('변경 이력을 불러오지 못했습니다.')
    } finally {
      setIsLoadingAuditLog(false)
    }
  }, [adminToken, authHeaders])

  useEffect(() => {
    loadCurrentAdmin()
    loadAllAdmins()
  }, [loadCurrentAdmin, loadAllAdmins])

  useEffect(() => {
    if (isSuperAdmin) {
      loadPendingAdmins()
      loadAuditLog()
    }
  }, [isSuperAdmin, loadPendingAdmins, loadAuditLog])

  const handleApprovePendingAdmin = async (pendingAdminId: number) => {
    if (processingAdminId !== null) {
      return
    }
    setProcessingAdminId(pendingAdminId)
    try {
      const response = await fetch(`${MainURL}/admin/pending-admins/${pendingAdminId}/approve`, {
        method: 'POST',
        headers: authHeaders,
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '승인에 실패했습니다.')
        return
      }
      setPendingAdmins((current) => current.filter((admin) => admin.id !== pendingAdminId))
      showToast(data?.message || '승인되었습니다.')
      await Promise.all([loadAllAdmins(), loadAuditLog()])
    } catch (error) {
      showToast('승인에 실패했습니다.')
    } finally {
      setProcessingAdminId(null)
    }
  }

  const handleRejectPendingAdmin = async (pendingAdminId: number) => {
    if (processingAdminId !== null) {
      return
    }
    setProcessingAdminId(pendingAdminId)
    try {
      const response = await fetch(`${MainURL}/admin/pending-admins/${pendingAdminId}`, {
        method: 'DELETE',
        headers: authHeaders,
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '거절에 실패했습니다.')
        return
      }
      setPendingAdmins((current) => current.filter((admin) => admin.id !== pendingAdminId))
      showToast(data?.message || '거절되었습니다.')
      await Promise.all([loadAllAdmins(), loadAuditLog()])
    } catch (error) {
      showToast('거절에 실패했습니다.')
    } finally {
      setProcessingAdminId(null)
    }
  }

  const handleToggleSuperAdmin = async (adminId: number, nextIsSuperAdmin: boolean) => {
    if (processingSuperAdminId !== null) {
      return
    }
    setProcessingSuperAdminId(adminId)
    try {
      const response = await fetch(`${MainURL}/admin/admins/${adminId}/super-admin`, {
        method: nextIsSuperAdmin ? 'POST' : 'DELETE',
        headers: authHeaders,
      })
      const data = await response.json().catch(() => null)
      const failureMessage = nextIsSuperAdmin ? '최고관리자 지정에 실패했습니다.' : '최고관리자 해제에 실패했습니다.'
      if (!response.ok) {
        showToast(data?.message || failureMessage)
        return
      }
      showToast(data?.message || (nextIsSuperAdmin ? '최고관리자로 지정되었습니다.' : '최고관리자 지정이 해제되었습니다.'))
      await Promise.all([loadAllAdmins(), loadPendingAdmins(), loadAuditLog()])
    } catch (error) {
      showToast(nextIsSuperAdmin ? '최고관리자 지정에 실패했습니다.' : '최고관리자 해제에 실패했습니다.')
    } finally {
      setProcessingSuperAdminId(null)
    }
  }

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (isSubmittingPassword) {
      return
    }
    if (newPasswordInput !== confirmPasswordInput) {
      showToast('새 비밀번호가 서로 일치하지 않습니다.')
      return
    }
    setIsSubmittingPassword(true)
    try {
      const response = await fetch(`${MainURL}/admin/me/password`, {
        method: 'PATCH',
        headers: jsonAuthHeaders,
        body: JSON.stringify({ currentPassword: currentPasswordInput, newPassword: newPasswordInput }),
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '비밀번호 변경에 실패했습니다.')
        return
      }
      showToast(data?.message || '비밀번호가 변경되었습니다.')
      setIsPasswordFormOpen(false)
      setCurrentPasswordInput('')
      setNewPasswordInput('')
      setConfirmPasswordInput('')
    } catch (error) {
      showToast('비밀번호 변경에 실패했습니다.')
    } finally {
      setIsSubmittingPassword(false)
    }
  }

  const resetCreateForm = () => {
    setIsCreateFormOpen(false)
    setCreateEmail('')
    setCreateName('')
    setCreatePassword('')
    setCreateRole('all')
    setCreateAllowedMenus([])
  }

  const handleCreateSubAdminSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (isSubmittingCreate) {
      return
    }
    setIsSubmittingCreate(true)
    try {
      const response = await fetch(`${MainURL}/admin/sub-admins`, {
        method: 'POST',
        headers: jsonAuthHeaders,
        body: JSON.stringify({
          email: createEmail,
          name: createName,
          password: createPassword,
          role: createRole,
          allowedMenus: createRole === 'custom' ? createAllowedMenus : undefined,
        }),
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '부계정 등록에 실패했습니다.')
        return
      }
      showToast(data?.message || '부계정이 등록되었습니다.')
      resetCreateForm()
      await Promise.all([loadAllAdmins(), loadAuditLog()])
    } catch (error) {
      showToast('부계정 등록에 실패했습니다.')
    } finally {
      setIsSubmittingCreate(false)
    }
  }

  const openRoleEdit = (admin: AdminAccount) => {
    setEditingAdminId(admin.id)
    setEditRole(admin.role)
    setEditAllowedMenus(resolveAllowedMenus(admin.role, admin.allowed_menus))
  }

  const handleRoleEditSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (isSubmittingRoleEdit || editingAdminId === null) {
      return
    }
    setIsSubmittingRoleEdit(true)
    try {
      const response = await fetch(`${MainURL}/admin/admins/${editingAdminId}/role`, {
        method: 'PATCH',
        headers: jsonAuthHeaders,
        body: JSON.stringify({
          role: editRole,
          allowedMenus: editRole === 'custom' ? editAllowedMenus : undefined,
        }),
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '권한 수정에 실패했습니다.')
        return
      }
      showToast(data?.message || '권한이 수정되었습니다.')
      setEditingAdminId(null)
      await Promise.all([loadAllAdmins(), loadAuditLog()])
    } catch (error) {
      showToast('권한 수정에 실패했습니다.')
    } finally {
      setIsSubmittingRoleEdit(false)
    }
  }

  const handleSuspendToggle = async (admin: AdminAccount) => {
    if (processingLifecycleId !== null) {
      return
    }
    const nextAction = admin.is_suspended ? 'resume' : 'suspend'
    setProcessingLifecycleId(admin.id)
    try {
      const response = await fetch(`${MainURL}/admin/admins/${admin.id}/${nextAction}`, {
        method: 'POST',
        headers: authHeaders,
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '상태 변경에 실패했습니다.')
        return
      }
      showToast(data?.message || '상태가 변경되었습니다.')
      await Promise.all([loadAllAdmins(), loadAuditLog()])
    } catch (error) {
      showToast('상태 변경에 실패했습니다.')
    } finally {
      setProcessingLifecycleId(null)
    }
  }

  const handleDeleteAdmin = async (admin: AdminAccount) => {
    if (processingLifecycleId !== null) {
      return
    }
    if (!window.confirm(`${admin.name} (${admin.email}) 계정을 삭제할까요?`)) {
      return
    }
    setProcessingLifecycleId(admin.id)
    try {
      const response = await fetch(`${MainURL}/admin/admins/${admin.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      })
      const data = await response.json().catch(() => null)
      if (!response.ok) {
        showToast(data?.message || '계정 삭제에 실패했습니다.')
        return
      }
      showToast(data?.message || '계정이 삭제되었습니다.')
      await Promise.all([loadAllAdmins(), loadAuditLog()])
    } catch (error) {
      showToast('계정 삭제에 실패했습니다.')
    } finally {
      setProcessingLifecycleId(null)
    }
  }

  const handleOpenSpecification = () => {
    const opened = openPageSpecification(adminSettingsSpecification, adminSettingsDataModelGuide, adminSettingsImplementationGuide)
    if (!opened) {
      showToast('화면 사양서 창을 열 수 없습니다. 팝업 차단을 해제해 주세요.')
    }
  }

  const subAdminAccounts = allAdmins.filter((admin) => !admin.is_super_admin)
  const activeSubAdmins = subAdminAccounts.filter((admin) => admin.is_active && !admin.is_suspended)
  const suspendedSubAdmins = subAdminAccounts.filter((admin) => admin.is_suspended)

  const filteredAdmins = allAdmins.filter((admin) => {
    const term = searchTerm.trim().toLowerCase()
    const matchesSearch = !term || admin.name.toLowerCase().includes(term) || admin.email.toLowerCase().includes(term)
    const matchesRole = !roleFilter || admin.role === roleFilter
    const matchesStatus = !statusFilter || adminStatusLabel(admin) === statusFilter
    return matchesSearch && matchesRole && matchesStatus
  })

  return (
    <div className='AdminSettings'>
      <div className='app-shell'>
        <Sidebar />

        <main className='workspace'>
          <header className='topbar'>
            <div className='title-area'>
              <h1>관리자 설정</h1>
            </div>
          </header>

          <section className='view active'>
            <section className='panel admin-account-panel'>
              <div className='panel-header'>
                <div>
                  <h2>관리자 계정</h2>
                  <p>현재 로그인한 관리자 계정의 보안 정보를 관리합니다.</p>
                </div>
              </div>
              <div className='admin-account-row'>
                <div>
                  <strong>{currentAdmin?.email ?? '불러오는 중...'}</strong>
                  {currentAdmin?.name ? <span>{currentAdmin.name}</span> : null}
                </div>
                <button className='primary-button' type='button' onClick={() => setIsPasswordFormOpen((open) => !open)}>
                  <KeyRound size={15} />
                  비밀번호 변경
                </button>
              </div>
              {isPasswordFormOpen && (
                <form className='inline-form' onSubmit={handlePasswordSubmit}>
                  <label className='form-field'>
                    현재 비밀번호
                    <input
                      type='password'
                      value={currentPasswordInput}
                      onChange={(event) => setCurrentPasswordInput(event.target.value)}
                      required
                    />
                  </label>
                  <label className='form-field'>
                    새 비밀번호
                    <input
                      type='password'
                      value={newPasswordInput}
                      onChange={(event) => setNewPasswordInput(event.target.value)}
                      minLength={8}
                      required
                    />
                  </label>
                  <label className='form-field'>
                    새 비밀번호 확인
                    <input
                      type='password'
                      value={confirmPasswordInput}
                      onChange={(event) => setConfirmPasswordInput(event.target.value)}
                      minLength={8}
                      required
                    />
                  </label>
                  <div className='inline-form-actions'>
                    <button className='secondary-button' type='button' onClick={() => setIsPasswordFormOpen(false)}>
                      취소
                    </button>
                    <button className='primary-button' type='submit' disabled={isSubmittingPassword}>
                      저장
                    </button>
                  </div>
                </form>
              )}
            </section>

            {isSuperAdmin && (
              <section className='panel pending-admins-panel'>
                <div className='panel-header'>
                  <div>
                    <h2>가입 승인 대기</h2>
                    <p>회원가입으로 신청된 관리자 계정을 승인하거나 거절합니다. 최고관리자만 처리할 수 있습니다.</p>
                  </div>
                </div>
                {isLoadingPendingAdmins ? (
                  <p className='pending-admins-empty'>불러오는 중...</p>
                ) : pendingAdmins.length === 0 ? (
                  <p className='pending-admins-empty'>승인 대기 중인 계정이 없습니다.</p>
                ) : (
                  <ul className='pending-admins-list'>
                    {pendingAdmins.map((pendingAdmin) => (
                      <li key={pendingAdmin.id} className='pending-admin-row'>
                        <div>
                          <strong>{pendingAdmin.name}</strong>
                          <span>{pendingAdmin.email}</span>
                          <span>{pendingAdmin.signup_ip || 'IP 미확인'}</span>
                        </div>
                        <div className='pending-admin-actions'>
                          <button
                            className='primary-button'
                            type='button'
                            disabled={processingAdminId === pendingAdmin.id}
                            onClick={() => handleApprovePendingAdmin(pendingAdmin.id)}
                          >
                            승인
                          </button>
                          <button
                            className='secondary-button'
                            type='button'
                            disabled={processingAdminId === pendingAdmin.id}
                            onClick={() => handleRejectPendingAdmin(pendingAdmin.id)}
                          >
                            거절
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {isSuperAdmin && (
              <div className='admin-account-metrics'>
                <article>
                  <span>전체 부계정</span>
                  <strong>{subAdminAccounts.length}명</strong>
                  <small>주관리자 제외</small>
                </article>
                <article>
                  <span>사용중</span>
                  <strong>{activeSubAdmins.length}명</strong>
                  <small>로그인 가능 계정</small>
                </article>
                <article>
                  <span>사용중지</span>
                  <strong>{suspendedSubAdmins.length}명</strong>
                  <small>로그인 및 세션 차단</small>
                </article>
              </div>
            )}

            <section className='panel admin-list-panel'>
              <div className='panel-header'>
                <div>
                  <h2>{isSuperAdmin ? '부계정 관리' : '전체 관리자 목록'}</h2>
                  <p>담당 업무에 필요한 메뉴만 허용하고 계정 상태와 접속 이력을 관리합니다.</p>
                </div>
                {isSuperAdmin && (
                  <button className='primary-button' type='button' onClick={() => setIsCreateFormOpen((open) => !open)}>
                    <UserPlus size={15} />
                    부계정 등록
                  </button>
                )}
              </div>

              {isSuperAdmin && isCreateFormOpen && (
                <form className='inline-form' onSubmit={handleCreateSubAdminSubmit}>
                  <label className='form-field'>
                    이메일
                    <input type='email' value={createEmail} onChange={(event) => setCreateEmail(event.target.value)} required />
                  </label>
                  <label className='form-field'>
                    이름
                    <input type='text' value={createName} onChange={(event) => setCreateName(event.target.value)} required />
                  </label>
                  <label className='form-field'>
                    초기 비밀번호
                    <input
                      type='password'
                      value={createPassword}
                      onChange={(event) => setCreatePassword(event.target.value)}
                      minLength={8}
                      required
                    />
                  </label>
                  <RoleMenuFields
                    role={createRole}
                    allowedMenus={createAllowedMenus}
                    onRoleChange={setCreateRole}
                    onAllowedMenusChange={setCreateAllowedMenus}
                  />
                  <div className='inline-form-actions'>
                    <button className='secondary-button' type='button' onClick={resetCreateForm}>
                      취소
                    </button>
                    <button className='primary-button' type='submit' disabled={isSubmittingCreate}>
                      등록
                    </button>
                  </div>
                </form>
              )}

              {isSuperAdmin && (
                <div className='toolbar compact-toolbar admin-account-toolbar'>
                  <div className='filter-group'>
                    <label className='inline-search'>
                      <input
                        type='search'
                        placeholder='이름 또는 이메일'
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                      />
                    </label>
                    <select className='filter' value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
                      <option value=''>역할 전체</option>
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <select className='filter' value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                      <option value=''>상태 전체</option>
                      <option value='승인 대기'>승인 대기</option>
                      <option value='사용중'>사용중</option>
                      <option value='사용중지'>사용중지</option>
                    </select>
                  </div>
                </div>
              )}

              {isLoadingAllAdmins ? (
                <p className='pending-admins-empty'>불러오는 중...</p>
              ) : filteredAdmins.length === 0 ? (
                <p className='pending-admins-empty'>조건에 맞는 관리자 계정이 없습니다.</p>
              ) : (
                <div className='table-wrap'>
                  <table className='sub-admin-table'>
                    <thead>
                      <tr>
                        <th>관리자</th>
                        <th>역할</th>
                        <th>허용 메뉴</th>
                        <th>상태</th>
                        <th>최근 접속</th>
                        <th>등록일</th>
                        {isSuperAdmin && <th>관리</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAdmins.map((admin) => (
                        <React.Fragment key={admin.id}>
                          <tr>
                            <td>
                              <div className='identity-cell'>
                                <strong>{admin.name}</strong>
                                <span>{admin.email}</span>
                              </div>
                            </td>
                            <td>
                              {admin.is_super_admin ? <span className='super-admin-badge'>최고관리자</span> : roleLabel(admin.role)}
                            </td>
                            <td>
                              {admin.is_super_admin
                                ? '전체'
                                : resolveAllowedMenus(admin.role, admin.allowed_menus).map(menuLabel).join(', ') || '없음'}
                            </td>
                            <td>
                              <span className={`status-badge ${adminStatusBadgeClass(admin)}`}>{adminStatusLabel(admin)}</span>
                            </td>
                            <td>{admin.last_login_at ?? '기록 없음'}</td>
                            <td>{admin.created_at ?? '-'}</td>
                            {isSuperAdmin && (
                              <td>
                                <div className='pending-admin-actions'>
                                  {admin.is_active ? (
                                    <button
                                      className={admin.is_super_admin ? 'secondary-button' : 'primary-button'}
                                      type='button'
                                      disabled={processingSuperAdminId === admin.id}
                                      onClick={() => handleToggleSuperAdmin(admin.id, !admin.is_super_admin)}
                                    >
                                      {admin.is_super_admin ? '최고관리자 해제' : '최고관리자 지정'}
                                    </button>
                                  ) : null}
                                  {!admin.is_super_admin && admin.is_active && (
                                    <>
                                      <button className='secondary-button' type='button' onClick={() => openRoleEdit(admin)}>
                                        <Pencil size={15} />
                                        수정
                                      </button>
                                      <button
                                        className='icon-button'
                                        type='button'
                                        title={admin.is_suspended ? '사용중지 해제' : '사용중지'}
                                        disabled={processingLifecycleId === admin.id}
                                        onClick={() => handleSuspendToggle(admin)}
                                      >
                                        {admin.is_suspended ? <PlayCircle size={15} /> : <Ban size={15} />}
                                      </button>
                                      <button
                                        className='icon-button danger'
                                        type='button'
                                        title='삭제'
                                        disabled={processingLifecycleId === admin.id}
                                        onClick={() => handleDeleteAdmin(admin)}
                                      >
                                        <Trash2 size={15} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                          {editingAdminId === admin.id && (
                            <tr className='role-edit-row'>
                              <td colSpan={isSuperAdmin ? 7 : 6}>
                                <form className='inline-form' onSubmit={handleRoleEditSubmit}>
                                  <RoleMenuFields
                                    role={editRole}
                                    allowedMenus={editAllowedMenus}
                                    onRoleChange={setEditRole}
                                    onAllowedMenusChange={setEditAllowedMenus}
                                  />
                                  <div className='inline-form-actions'>
                                    <button className='secondary-button' type='button' onClick={() => setEditingAdminId(null)}>
                                      취소
                                    </button>
                                    <button className='primary-button' type='submit' disabled={isSubmittingRoleEdit}>
                                      저장
                                    </button>
                                  </div>
                                </form>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {isSuperAdmin && (
              <section className='panel admin-audit-panel'>
                <div className='panel-header'>
                  <div>
                    <h2>계정 변경 이력</h2>
                    <p>부계정 등록과 권한·상태·비밀번호 변경 기록을 확인합니다.</p>
                  </div>
                </div>
                {isLoadingAuditLog ? (
                  <p className='pending-admins-empty'>불러오는 중...</p>
                ) : auditLog.length === 0 ? (
                  <p className='pending-admins-empty'>변경 이력이 없습니다.</p>
                ) : (
                  <div className='table-wrap'>
                    <table className='admin-audit-table'>
                      <thead>
                        <tr>
                          <th>일시</th>
                          <th>처리자</th>
                          <th>대상 계정</th>
                          <th>변경 내용</th>
                          <th>결과</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLog.map((entry, index) => (
                          <tr key={`${entry.created_at}-${index}`}>
                            <td>{entry.created_at}</td>
                            <td>{entry.actor_label}</td>
                            <td>{entry.target_label}</td>
                            <td>
                              {AUDIT_ACTION_LABELS[entry.action] ?? entry.action}
                              {entry.detail ? ` · ${entry.detail}` : ''}
                            </td>
                            <td>
                              <span className={`status-badge${entry.result === 'success' ? ' active' : ''}`}>
                                {entry.result === 'success' ? '성공' : '실패'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </section>
        </main>
      </div>

      <button
        className='page-spec-button'
        type='button'
        aria-label='현재 화면 사양서 열기'
        title='현재 화면 사양서'
        onClick={handleOpenSpecification}
      >
        <HelpCircle size={22} />
      </button>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default AdminSettings
